import moment from 'moment';
import { DateIntervalType, toDateInterval } from './DateInterval';

type serverDate = {
    date: string;
    timezone: string;
    timezone_type: 1 | 3;
};

export type DateLike = string | Date | moment.Moment | serverDate | null;

export const DATE_FORMATS = {
    pretty: 'MMMM Do, YYYY', // January 5th, 2019
    pretty_long: 'MMMM DD, YYYY', // January 05, 2019
    pretty_short: 'MMM DD, YYYY', // Jan 05, 2019
    pretty_short_time: 'MMM DD, YYYY hh:mm A zz', // Jan 05, 2019 12:35 PM EST
    human: 'll - LT', // Apr 13, 2019 - 8:37 PM
    humanAt: 'll [at] LT', // Apr 13, 2019 at 8:37 PM
    submittedAt: '[Submitted] ll [at] LT', // Submitted Apr 13, 2019 at 8:37 PM
    submittedOn: '[Submitted] [on] ll', // Submitted Apr 13, 2019 at 8:37 PM
    postedDate: 'll', // Apr 13, 2019
    postedTime: 'LT', // 8:37 PM
    dbFormat: 'YYYY-MM-DD',
    table: 'MM/DD/YYYY',
    short_month_date: 'MMM DD', // Apr 13
    short_month_date_time: 'MMM DD, LT', // Apr 13, 8:37 PM
    orderedOn: '[Ordered On] ll',
    uploaded_at: 'MM/DD/YY [at] LT', // 09/13/23 at 8:37 AM
    ordinal_at: 'MMMM Do, YYYY [at] LT',
    pretty_shortest_time: 'MMM DD, hh:mm A zz', // Jan 05, 12:35 PM EST
};

/**
 * Get now time as Date
 * @returns {Date}
 */
export const nowDate = (): Date => new Date();

/**
 * Get now time
 * @returns {Moment.Moment}
 */
export const now = (): moment.Moment => moment(nowDate());

/**
 * Get unixTimestamp
 *
 * @param date
 * @param divided
 * @returns {number}
 */
export const unix = (date?: Date, divided?: boolean): number =>
    divided
        ? unix(date) / 1000
        : normalizeDate(date ? date : nowDate())
              .toDate()
              .getTime();

/**
 * Get timezone offset
 * @returns {number}
 */
export const timezoneOffset = (): number => nowDate().getTimezoneOffset();

const _fromString = (date: string) => {
    if (date && date.includes('T') && date.endsWith('Z')) {
        return moment(new Date(date));
    }

    return moment(date, 'YYYY-MM-DD HH:mm:ss');
};

const isServerDate = (date: any): date is serverDate =>
    date && typeof date === 'object' && date.date && date.timezone_type && date.timezone;

/**
 * Normalize Date
 * @see https://github.com/moment/moment/issues/3887#issuecomment-292799707
 * @param date
 * @return {*}
 */
export const normalizeDate = (date?: DateLike): moment.Moment => {
    if (!date) {
        return moment();
    }

    if (moment.isMoment(date)) {
        return date;
    }

    if (date instanceof Date) {
        return moment(date);
    }

    if (isServerDate(date)) {
        return normalizeDate(date.date);
    }

    return _fromString(date as string);
};

/**
 * Get Date formatted
 * @param format
 * @param date
 * @return {string}
 */
export const formatDate = (format: keyof typeof DATE_FORMATS | string, date?: DateLike): string =>
    normalizeDate(date).format(DATE_FORMATS[format as keyof typeof DATE_FORMATS] || format);

/**
 * Get difference between startDate and endDate
 * @example
 * const start = new Date();
 * setTimeout(() => {
 *     diff(start) // approx. 1450
 *     diff(start, new Date()) // approx. 1450
 * }, 1450);
 *
 * @param startDate
 * @param endDate
 * @returns {number}
 */
export const diff = (startDate: any, endDate?: any): number => unix(endDate || nowDate()) - unix(startDate);

/**
 *
 * @param interval
 */
export const humanizeDurationInterval = (interval?: string | Partial<DateIntervalType>): string => {
    if (interval) {
        if (typeof interval === 'string') {
            interval = toDateInterval(interval);
        }

        const days = interval?.d ?? 0;

        if (days > 0) {
            const weeks = days / 7;

            if (weeks < 1.0) {
                return `${days} ${days === 1 ? 'day' : 'days'}`;
            }

            let weeksStr = `${weeks}`;
            const diff = weeks - Math.floor(weeks);

            if (diff > 0.0) {
                weeksStr = `${Math.floor(weeks)}-${Math.ceil(weeks)}`;
            }

            return `${weeksStr} ${weeks === 1 ? 'week' : 'weeks'}`;
        }
    }

    return '';
};
